import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BasketService } from '@core/services/basket/basket.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { ProductDetailService } from '@features/product-detail/product-detail.service';
import { AddToBasketState, CampaignState } from '@features/product/product';
import { ProductActionModel } from '@features/product/product-actions/product-actions';
import { StoreStockSidebarType } from '@features/store-stock/store-stock';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { filter, map, take, takeWhile, tap, withLatestFrom } from 'rxjs/operators';
import { SettingsService } from '@core/services/settings.service';
import { GiftOptionsDialogResponse } from '@features/basket-shared/basket-gift-options-dialog/basket-gift-options-dialog.model';
import { ActiveCampaign } from '@features/product/product.models';
import { ActivatedRoute } from '@angular/router';
import { getCampaignByType } from '@features/campaigns/helper/get-campaign-by-type';
import { customFilterParams } from '@features/product-filter/product-filter.config';
import { PlatformService } from '@core/services/platform.service';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { IsPreOrderPipe } from '../pipes/is-pre-order.pipe';
import { GetCampaignTypePipe } from '../pipes/get-campaign-type.pipe';
import { ProductAvailablePipe } from '../pipes/product-available.pipe';
import { ProductReservablePipe } from '../pipes/product-reservable.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { AsyncPipe, NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { ProductDialogOverlayService } from '@features/product/product-dialog-overlay.service';
import { GetPrioritySplashPipe } from '@features/product/pipes/get-priority-splash.pipe';

@UntilDestroy()
@Component({
    selector: 'app-product-actions',
    templateUrl: './product-actions.component.html',
    styleUrls: ['./product-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgSwitch,
        NgSwitchCase,
        NgClass,
        LoadingDotsComponent,
        SvgIconComponent,
        NgIf,
        AsyncPipe,
        TranslateSafePipe,
        ProductReservablePipe,
        ProductAvailablePipe,
        GetPrioritySplashPipe,
        GetCampaignTypePipe,
        IsPreOrderPipe
    ],
    providers: [ProductDialogOverlayService]
})
export class ProductActionsComponent implements OnInit {
    public state = new BehaviorSubject<AddToBasketState>('idle');
    public campaignState = new BehaviorSubject<CampaignState>('idle');

    @Input()
    public line: ProductActionModel;

    @Input()
    public variant: 'simple' | 'normal' = 'normal';

    @Input()
    public amount = 1;

    @Input()
    public isNotifyMeEnabled = false;

    @Output() statusEvent: EventEmitter<AddToBasketState> = new EventEmitter();

    constructor(
        private readonly platformService: PlatformService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly basketService: BasketService,
        private readonly notificationService: NotificationService,
        private readonly productDialogOverlayService: ProductDialogOverlayService,
        private readonly productDetailService: ProductDetailService,
        private readonly stateService: SiteStateService,
        public storesService: StoresService,
        public settingsService: SettingsService
    ) {}

    ngOnInit(): void {
        this.basketService
            .watchBasketNotifications(this.state)
            .pipe(untilDestroyed(this))
            .subscribe(_ => {
                this.state.next('idle');
            });

        this.state.pipe(untilDestroyed(this)).subscribe(state => this.statusEvent.emit(state));

        // we only want to check for campaign if we are on the product page and the variant is normal
        if (this.variant === 'normal') {
            this.checkQueryParamsForCampaign();
        }
    }

    public addToBasket(options?: GiftOptionsDialogResponse): void {
        this.basketService.basketData$
            .pipe(
                filter(({ requestInProgress }) => !requestInProgress),
                map(({ basket }) =>
                    basket.lines.every(line => line.digitalProduct === (this.line.digitalProduct || false))
                ),
                tap(result => {
                    if (!result) {
                        this.notificationService.createNotification({
                            fadeTime: 10000,
                            translationHeadline: 'basket.notification.differentProductsHeadline',
                            translationText: 'basket.notification.differentProductsDescription'
                        });
                    }
                }),
                takeWhile(result => result),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.state.next('loading');
                this.basketService.addToBasket(this.line.variantNo, this.amount, options);
            });
    }

    public notifyMe(): void {
        this.productDialogOverlayService.notifyMe(this.line);
    }

    public openStockOverlay(type: StoreStockSidebarType): void {
        this.productDetailService
            .requestProduct(this.line.variantNo)
            .pipe(take(1))
            .subscribe(() => this.productDialogOverlayService.openStockOverlay(this.line, type));
    }

    public openCampaignOverlay(campaign?: ActiveCampaign, storeId?: string): void {
        this.campaignState.next('loading');
        this.productDialogOverlayService
            .openCampaignOverlay(this.line, storeId, campaign)
            .then(() => {
                this.campaignState.next('idle');
            })
            .catch(() => {
                this.campaignState.next('error');
            });
    }

    /**
     * Checks if the query params contain a campaignId and storeId and opens the campaign overlay if so
     * @private
     */
    private checkQueryParamsForCampaign(): void {
        const hasActiveCampaign = getCampaignByType(this.line?.activeCampaigns, 'Mixed');
        if (!hasActiveCampaign || this.platformService.isServer) {
            return;
        }

        this.activatedRoute.queryParams
            .pipe(
                withLatestFrom(this.stateService.state$),
                // we don't want to open the campaign overlay if the user is in a dialog.
                filter(([_, states]) => !states.dialog),
                map(([params, _]) => ({
                    activeCampaign: this.line?.activeCampaigns?.find(
                        campaign => campaign.campaignId === params.campaignId
                    ),
                    storeId: params[customFilterParams.storeId]
                })),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(({ activeCampaign, storeId }) => {
                this.openCampaignOverlay(activeCampaign, storeId);
            });
    }
}
